import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink, Info, Loader2, RefreshCw, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { PLATFORM_META } from "../lib/api";

/* Analytics shows what the platforms actually reported, and nothing else.

   Earlier versions invented reach and likes with a random generator at
   publish time. Now: publish outcomes come from the publish results,
   engagement from each platform's API, clicks from our own redirect. Where a
   platform reports nothing, the cell is a dash and the reason is written
   out - a blank is honest, a plausible-looking zero is not. */

const RANGES = [7, 30, 90];
const fmt = (n) => (n === null || n === undefined ? "—" : n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : String(n));
const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

// Headline values are stat tiles, set in the body face, in text colour.
const Stat = ({ label, value, sub }) => (
  <div className="glass rounded-2xl p-5">
    <p className="text-xs font-medium text-slate-400">{label}</p>
    <p className="mt-2 text-3xl font-bold text-slate-50 font-sans">{value}</p>
    {sub && <p className="mt-1 text-[11px] text-slate-500 leading-snug">{sub}</p>}
  </div>
);

const COLS = [["published", "Published"], ["failed", "Failed"], ["likes", "Likes"], ["comments", "Comments"],
  ["shares", "Shares"], ["reach", "Reach"], ["impressions", "Views"], ["link_clicks", "Link clicks"]];

export const AnalyticsView = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(() => api.get(`/analytics?days=${days}`).then(({ data }) => setData(data)), [days]);
  useEffect(() => { setData(null); load().catch(() => toast.error("Could not load analytics")); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data: r } = await api.post("/analytics/sync");
      await load();
      toast.success(r.synced ? `Refreshed metrics for ${r.synced} post${r.synced === 1 ? "" : "s"}` : "Nothing to refresh in this period");
    } catch { toast.error("Refresh failed"); } finally { setSyncing(false); }
  };

  const channels = Object.entries(data?.by_channel || {});
  const anyMeasured = (data?.measured_posts || 0) > 0;
  const t = data?.totals || {};

  return (
    <div className="space-y-8" data-testid="analytics-view">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold tracking-wider uppercase text-indigo-400 mb-2">Analytics</p>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-50">Performance</h1>
          <p className="text-sm text-slate-400 mt-1.5">
            Real numbers from the platforms. {data?.last_synced_at ? `Last read ${when(data.last_synced_at)}.` : "Read a few minutes after each post goes live, then every 6 hours."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-xl border border-slate-800 overflow-hidden">
            {RANGES.map((d) => (
              <button key={d} onClick={() => setDays(d)}
                className={`px-3.5 py-2 text-xs font-semibold ${days === d ? "bg-indigo-500/20 text-indigo-100" : "text-slate-400 hover:text-slate-200"}`}>
                {d} days
              </button>
            ))}
          </div>
          <button onClick={sync} disabled={syncing}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-700 text-sm font-semibold text-slate-200 hover:border-slate-600 disabled:opacity-60">
            <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} /> Refresh from platforms
          </button>
        </div>
      </div>

      {!data ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-indigo-400" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
            <Stat label="Posts published" value={data.published} sub={data.failed_posts ? `${data.failed_posts} failed everywhere` : "in this period"} />
            <Stat label="With platform metrics" value={data.measured_posts} sub="the rest: see notes below" />
            <Stat label="Likes & reactions" value={anyMeasured ? fmt(t.likes) : "—"} />
            <Stat label="Comments" value={anyMeasured ? fmt(t.comments) : "—"} />
            <Stat label="Reach" value={t.reach ? fmt(t.reach) : "—"} sub={t.reach ? "unique accounts" : "needs insights access"} />
            <Stat label="Engagement rate" value={data.engagement_rate === null ? "—" : `${data.engagement_rate}%`}
              sub={data.engagement_rate === null ? "only where reach is reported" : "of reached accounts"} />
            <Stat label="Clicks to your site" value={fmt(t.link_clicks)} sub="tracked links" />
          </div>

          {channels.length > 0 && (
            <div className="glass rounded-2xl overflow-hidden">
              <div className="px-6 pt-5 pb-3">
                <h3 className="font-heading font-bold text-slate-100">By channel</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-slate-400 border-y border-slate-800 bg-slate-950/40">
                    <tr>
                      <th className="text-left font-medium px-6 py-2.5">Channel</th>
                      {COLS.map(([k, l]) => <th key={k} className="text-right font-medium px-4 py-2.5 whitespace-nowrap">{l}</th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/70">
                    {channels.map(([plat, c]) => {
                      const measured = c.measured > 0;
                      return (
                        <tr key={plat}>
                          <td className="px-6 py-3">
                            <span className="flex items-center gap-2 font-semibold text-slate-100">
                              <span className="w-2 h-2 rounded-full" style={{ background: PLATFORM_META[plat]?.color || "#64748b" }} />
                              {PLATFORM_META[plat]?.label || plat}
                            </span>
                          </td>
                          {COLS.map(([k]) => {
                            const raw = c[k];
                            const isMetric = !["published", "failed", "link_clicks"].includes(k);
                            const shown = isMetric && !measured ? "—" : fmt(raw);
                            return <td key={k} className={`px-4 py-3 text-right ${shown === "—" ? "text-slate-600" : "text-slate-200"}`}>{shown}</td>;
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {channels.some(([, c]) => c.notes?.length) && (
                <div className="px-6 py-4 border-t border-slate-800 space-y-1.5">
                  {channels.flatMap(([plat, c]) => (c.notes || []).map((n) => (
                    <p key={plat + n} className="text-xs text-slate-400 flex items-start gap-2">
                      <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-500" />
                      <span><strong className="text-slate-300">{PLATFORM_META[plat]?.label || plat}:</strong> {n}</span>
                    </p>
                  )))}
                </div>
              )}
            </div>
          )}

          <div>
            <h3 className="font-heading font-bold text-slate-100 mb-3">Posts</h3>
            {data.posts.length === 0 ? (
              <div className="glass rounded-2xl py-14 text-center text-sm text-slate-500">Nothing published in the last {days} days.</div>
            ) : (
              <div className="space-y-2">
                {data.posts.map((p) => (
                  <motion.div key={p.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                    className="glass rounded-2xl p-3 flex gap-3">
                    <div className="w-16 h-16 rounded-xl overflow-hidden bg-slate-900 shrink-0 flex items-center justify-center">
                      {p.image_url ? <img src={p.image_url} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-4 h-4 text-slate-600" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-3">
                        <p className="text-sm font-semibold text-slate-100 truncate">{p.topic}</p>
                        <span className="text-[11px] text-slate-500 whitespace-nowrap">{when(p.posted_at)}</span>
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-x-5 gap-y-1">
                        {p.channels.map((c, i) => (
                          <span key={i} className="text-xs text-slate-400 flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: PLATFORM_META[c.platform]?.color || "#64748b" }} />
                            <span className="text-slate-300">{c.account}</span>
                            {c.status === "failed" ? <span className="text-rose-300">failed</span>
                              : c.available ? (
                                <>
                                  <span>{fmt(c.likes)} likes</span><span>{fmt(c.comments)} comments</span>
                                  {c.reach !== undefined && <span>{fmt(c.reach)} reach</span>}
                                  {c.permalink && <a href={c.permalink} target="_blank" rel="noreferrer" className="text-indigo-300"><ExternalLink className="w-3 h-3" /></a>}
                                </>
                              ) : <span className="text-slate-600" title={c.reason || ""}>no metrics{c.mode === "simulated" ? " (demo)" : ""}</span>}
                          </span>
                        ))}
                        {p.link_clicks > 0 && <span className="text-xs text-violet-300">{p.link_clicks} clicks to site</span>}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
